import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    first,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { CycleCountTask } from '../models/cycle-count-task.model';
import {
    AuditDto,
    AuditFacade,
    ExceptionDto,
    InventoryBundleFacade,
    WarehouseFacade,
} from '@pf/platform-wms/domain';
import { LoggerService } from '@pf/shared-services';
import { ProfileProvider } from '@pf/shared-common';
import { CycleCountTaskOrchestratorService } from '../services/cycle-count-task-orchestrator.service';
import { RoutesForCycleCounts } from './cycle-counts.routes';
import { CycleCountContext } from '../models/cycle-count-context.model';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { asArraySafe, asNumberSafe } from '@pf/shared-utility';
import {
    AuditCount,
    ExceptionStatus,
    InventoryAdjustmentReason,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';
import { ExceptionsFacade } from './exceptions.facade';
import {
    filterAuditsByZones,
    taskHasAuditInZones,
} from './cycle-count-filter.util';

@Injectable()
@UntilDestroy()
export class CycleCountFacade {
    private readonly userPositionFacade = inject(UserPositionFacade);
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly auditFacade = inject(AuditFacade);
    private readonly logger = inject(LoggerService);
    private readonly profileProvider = inject(ProfileProvider);
    private readonly orchestrator = inject(CycleCountTaskOrchestratorService);
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly routes = inject(RoutesForCycleCounts);
    private readonly inventoryBundleFacade = inject(InventoryBundleFacade);
    private readonly exceptionsFacade = inject(ExceptionsFacade);

    private tasksLoadingState = new BehaviorSubject<
        'not-loaded' | 'loading' | 'loaded'
    >('not-loaded');
    private _activeAudit = new BehaviorSubject<AuditDto | null>(null);

    private filteredTasks$ = combineLatest([
        this.taskStateService.tasks$,
        this.taskStateService.taskFilters$,
    ]).pipe(
        map(([tasks, filters]) => {
            return (tasks as CycleCountTask[])
                .filter(task => taskHasAuditInZones(task.audits, filters.zones))
                .map(task => {
                    if (!filters.zones?.length) {
                        return task;
                    }
                    const filteredAudits = filterAuditsByZones(
                        task.audits,
                        filters.zones
                    );
                    const zoneCount = new Set(
                        filteredAudits
                            .map(
                                a =>
                                    a.inventoryBundle?.container?.slot?.zone?.id
                            )
                            .filter(Boolean)
                    ).size;
                    const slotCount = new Set(
                        filteredAudits
                            .map(a => a.inventoryBundle?.container?.slot?.id)
                            .filter(Boolean)
                    ).size;
                    const title =
                        filteredAudits.length > 1
                            ? `${filteredAudits.length} Cycle Counts`
                            : `Cycle Count ${filteredAudits[0]?.item?.sku}`;
                    return {
                        ...task,
                        title,
                        details: `${slotCount} Slots in ${zoneCount} Zones`,
                    };
                });
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    loading$ = this.tasksLoadingState
        .asObservable()
        .pipe(map(state => state !== 'loaded'));

    activeCycleCountTask$: Observable<CycleCountTask> =
        this.taskStateService.activeTask$.pipe(
            map(task => task as CycleCountTask),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    displayAuditsForActiveTask$: Observable<AuditDto[]> = combineLatest([
        this.taskStateService.activeTask$,
        this.taskStateService.taskFilters$,
    ]).pipe(
        map(([task, filters]) =>
            filterAuditsByZones(
                (task as CycleCountTask | null | undefined)?.audits,
                filters.zones
            )
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    todaysCycleCounts = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'today')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    futureCycleCounts$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'future')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    activeCycleCounts$ = this.filteredTasks$.pipe(
        map(tasks =>
            (tasks as CycleCountTask[]).filter(
                task => task.section === 'active'
            )
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    canClaimMoreAudits$ = this.filteredTasks$.pipe(
        map(tasks => {
            const nonActiveTasks = (tasks as CycleCountTask[]).filter(
                task => task.section !== 'active'
            );
            return nonActiveTasks.length > 0;
        })
    );

    getUserSessionAudits$() {
        return this.userPositionFacade.active$.pipe(
            filter(Boolean),
            map(userPosition =>
                asArraySafe(
                    (userPosition!.context as CycleCountContext).auditIds
                )
            ),
            distinctUntilChanged(
                (a, b) => JSON.stringify(a) === JSON.stringify(b)
            ),
            switchMap(auditIds => {
                if (!auditIds.length) {
                    return of([]);
                }

                return this.auditFacade.getMany$(auditIds);
            })
        );
    }

    loadCycleCounts$() {
        this.tasksLoadingState.next('loading');
        this.taskStateService.clearTasks();
        this.auditFacade.listenForAuditUpdates();

        return combineLatest([
            this.auditFacade.getActiveAudits$(),
            this.getUserSessionAudits$(),
        ]).pipe(
            untilDestroyed(this),
            debounceTime(300),
            tap(([activeAudits, userActiveAudits]) => {
                const activeTask =
                    this.taskStateService.getActiveTask<CycleCountTask>();
                if (activeTask?.claimedAt) {
                    return;
                }
                this.orchestrator.orchestrate(
                    this.warehouseFacade.active!,
                    activeAudits.filter(a => !a.isDeleted),
                    userActiveAudits.filter(a => !a.isDeleted)
                );
            }),
            tap(() => {
                this.tasksLoadingState.next('loaded');
            }),
            map(() => true),
            catchError(err => {
                this.logger.error(err);
                return of(false);
            })
        );
    }

    loadActiveCycleCountTask$() {
        if (this.taskStateService.getActiveTask()) {
            return of(true);
        }

        return this.tasksLoadingState.pipe(
            filter(state => state !== 'loading'),
            take(1),
            switchMap(state => {
                if (state === 'not-loaded') {
                    return this.loadCycleCounts$().pipe(delay(100));
                }

                return of(true);
            }),
            switchMap(() => this.activeCycleCounts$),
            tap(tasks => {
                if (tasks.length === 0) {
                    this.clearActiveCycleCountTask();
                } else {
                    this.taskStateService.setActiveTask(tasks[0].id);
                }
            }),
            map(() => true)
        );
    }

    clearActiveCycleCountTask() {
        this.taskStateService.clearActiveTask();
        this.navigateToQueue();
    }

    setActiveCycleCountTask(id: string) {
        if (!id) {
            this.clearActiveCycleCountTask();
            return;
        }

        this.taskStateService.setActiveTask(id);
        this.routes.perform().navigate();
    }

    claimActiveCycleCountTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: CycleCountTask) => {
                const zoneIds = this.taskStateService.getTaskFilters().zones;
                const auditsToClaim = filterAuditsByZones(
                    activeTask.audits,
                    zoneIds
                );
                if (!auditsToClaim.length) {
                    return of(false);
                }
                const auditIds = auditsToClaim.map(a => a.id);
                return this.auditFacade.claimAudits$(auditIds).pipe(
                    tap(() => {
                        this.taskStateService.claimActiveTask();
                    }),
                    tap(() => {
                        this.taskStateService
                            .updateTaskContext$<CycleCountContext>(
                                activeTask.id,
                                {
                                    auditIds: auditIds,
                                }
                            )
                            .subscribe();
                    }),
                    map(() => true),
                    catchError(() => {
                        return this.releaseActiveCycleCountTask$().pipe(
                            tap(() =>
                                this.loadCycleCounts$()
                                    .pipe(first())
                                    .subscribe()
                            )
                        );
                    })
                );
            },
            of(false)
        );
    }

    claimNextAuditTask$() {
        return this.filteredTasks$.pipe(
            first(),
            map(tasks => {
                const nonActiveTasks = (tasks as CycleCountTask[]).filter(
                    task => task.section !== 'active'
                );
                if (!nonActiveTasks.length) {
                    return null;
                }

                return nonActiveTasks[0];
            }),
            switchMap((task: CycleCountTask | null) => {
                if (task) {
                    this.setActiveCycleCountTask(task.id);
                    return this.claimActiveCycleCountTask$();
                }
                return of(false);
            })
        );
    }

    releaseActiveCycleCountTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: CycleCountTask) => {
                const fromContext = asArraySafe(activeTask.context?.auditIds);
                const auditIdsToRelease =
                    fromContext.length > 0
                        ? fromContext
                        : filterAuditsByZones(
                              activeTask.audits,
                              this.taskStateService.getTaskFilters().zones
                          ).map(a => a.id);
                if (!auditIdsToRelease.length) {
                    return of(false);
                }
                return this.auditFacade.releaseAudits$(auditIdsToRelease).pipe(
                    tap(() => {
                        this.resetContext(activeTask.id);
                    }),
                    tap(() => {
                        this.clearActiveCycleCountTask();
                    }),
                    map(() => true),
                    catchError(() => of(false))
                );
            },
            of(false)
        );
    }

    navigateToQueue() {
        this.routes.queue().navigate();
    }

    completeTask$() {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: CycleCountTask) => {
                this.resetContext(activeTask.id);
                return of(true).pipe(
                    tap(() => {
                        this.taskStateService.clearActiveTask();
                        this.navigateToQueue();
                    })
                );
            },
            of(false)
        );
    }

    setActiveAuditId(taskId: string, audit: AuditDto) {
        this.taskStateService
            .performWithActiveTask$((task: CycleCountTask) => {
                if (task.context.activeAuditId !== audit.id) {
                    this.taskStateService
                        .updateTaskContext$(taskId, {
                            activeAuditId: audit.id,
                        })
                        .subscribe();
                }
                this._activeAudit.next(audit);
                return of(true);
            }, of(false))
            .subscribe();
    }

    addAuditCount$(auditId: string, auditCount: AuditCount) {
        return this.auditFacade.addAuditCount$(auditId, auditCount);
    }

    completeAudit$(auditId: string, auditCount: AuditCount) {
        const audit = this.auditFacade.getById(auditId);
        if (!audit) {
            throw new Error('Audit not found');
        }
        const discrepancy =
            asNumberSafe(auditCount.actualQuantity) -
            asNumberSafe(auditCount.auditQuantity);
        let completeObs$ = this.addAuditCount$(auditId, auditCount);
        if (discrepancy !== 0) {
            completeObs$ = this.addExceptionAndAdjustInventory$(
                completeObs$,
                discrepancy,
                auditCount
            );
        }

        return completeObs$.pipe(
            switchMap(() => {
                return this.auditFacade.completeAudit$(auditId);
            })
        );
    }

    private addExceptionAndAdjustInventory$(
        completeObs$: Observable<AuditDto>,
        discrepancy: number,
        auditCount: AuditCount
    ) {
        return completeObs$.pipe(
            switchMap((audit: AuditDto) => {
                return this.exceptionsFacade
                    .createException$(
                        new ExceptionDto({
                            status: ExceptionStatus.Open,
                            sourceEntity: WmsPlatformEntity.Audit,
                            sourceEntityId: audit.id,
                            exceptionType: {
                                id: '51DE23C5-579E-4751-B8CF-08DE75492913',
                            },
                            priority: 'Medium',
                            warehouse: { id: audit.warehouse!.id },
                            slot: audit.inventoryBundle?.container?.slot,
                            item: audit.item,
                            inventoryBundle: audit.inventoryBundle,
                            expectedQuantity: asNumberSafe(
                                auditCount.auditQuantity
                            ),
                            actualQuantity: asNumberSafe(
                                auditCount.actualQuantity
                            ),
                            affectedQuantity: Math.abs(discrepancy),
                        }),
                        []
                    )
                    .pipe(
                        switchMap((exception: ExceptionDto) => {
                            return this.inventoryBundleFacade
                                .adjustInventoryForException$(
                                    audit.inventoryBundle!.id as string,
                                    discrepancy,
                                    exception.id,
                                    InventoryAdjustmentReason.Audit
                                )
                                .pipe(map(() => audit));
                        }),
                        map(() => audit)
                    );
            })
        );
    }

    private resetContext(activeTaskId: string) {
        this.taskStateService
            .updateTaskContext$(activeTaskId, {
                auditIds: [],
                activeAuditId: '',
            })
            .subscribe();
    }
}
